import React from 'react';
import {
  Box,
  Container,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import { COMPANY_INFO } from '../utils/constants';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Box sx={{ py: 8, backgroundColor: 'background.default' }}>
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
            Politique de Confidentialité
          </Typography>

          <Typography variant="body1" paragraph sx={{ mb: 4, fontStyle: 'italic' }}>
            Cette politique de confidentialité explique comment EZTECH SOLUTIONS collecte, utilise et protège vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD).
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              1. Responsable du traitement
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>EZTECH SOLUTIONS</strong><br />
              SARL au capital variable<br />
              SIREN : {COMPANY_INFO.siren}<br />
              Siège social : {COMPANY_INFO.address.street}, {COMPANY_INFO.address.postalCode} {COMPANY_INFO.address.city}<br />
              Email : contact@eztech-solutions.fr
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              2. Données collectées
            </Typography>
            <Typography variant="body1" paragraph>
              Nous collectons uniquement les données nécessaires dans les cas suivants :
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Formulaire de contact"
                  secondary="Nom, prénom, adresse e-mail, sujet et message"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Données de navigation"
                  secondary="Adresse IP, type de navigateur, pages visitées (uniquement si nécessaire pour le fonctionnement du site)"
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              3. Finalités du traitement
            </Typography>
            <Typography variant="body1" paragraph>
              Vos données personnelles sont traitées pour les finalités suivantes :
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Répondre à vos demandes"
                  secondary="Traitement de vos demandes de contact, devis ou informations"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Amélioration du service"
                  secondary="Analyse de l'utilisation du site pour améliorer nos services"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Obligations légales"
                  secondary="Respect des obligations légales et réglementaires"
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              4. Base légale du traitement
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Intérêt légitime"
                  secondary="Pour le traitement des demandes de contact et l'amélioration de nos services"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Consentement"
                  secondary="Lorsque vous nous contactez via le formulaire de contact"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Exécution d'un contrat"
                  secondary="Pour la réalisation de prestations demandées"
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              5. Destinataires des données
            </Typography>
            <Typography variant="body1" paragraph>
              Vos données personnelles sont destinées à :
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="EZTECH SOLUTIONS et ses employés autorisés" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Nos prestataires techniques (hébergement) dans le cadre strict de leurs missions" />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              <strong>Nous ne vendons, ne louons, ni ne transmettons vos données à des tiers à des fins commerciales.</strong>
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              6. Durée de conservation
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Données de contact"
                  secondary="Conservées pendant 3 ans à compter du dernier contact"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Données de navigation"
                  secondary="Conservées pendant 13 mois maximum"
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              7. Vos droits
            </Typography>
            <Typography variant="body1" paragraph>
              Conformément au RGPD, vous disposez des droits suivants :
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Droit d'accès"
                  secondary="Connaître les données vous concernant"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Droit de rectification"
                  secondary="Corriger des données inexactes"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Droit à l'effacement"
                  secondary="Supprimer vos données dans certains cas"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Droit d'opposition"
                  secondary="Vous opposer au traitement de vos données"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Droit à la portabilité"
                  secondary="Récupérer vos données dans un format structuré"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Droit de limitation"
                  secondary="Limiter le traitement de vos données"
                />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              <strong>Pour exercer ces droits, contactez-nous à :</strong> contact@eztech-solutions.fr
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              8. Sécurité des données
            </Typography>
            <Typography variant="body1" paragraph>
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre :
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="L'accès non autorisé" />
              </ListItem>
              <ListItem>
                <ListItemText primary="La divulgation" />
              </ListItem>
              <ListItem>
                <ListItemText primary="La modification" />
              </ListItem>
              <ListItem>
                <ListItemText primary="La destruction" />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              9. Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              Notre site peut utiliser des cookies strictement nécessaires au fonctionnement du site. Aucun cookie de tracking ou publicitaire n'est utilisé.
            </Typography>
            <Typography variant="body1" paragraph>
              Vous pouvez configurer votre navigateur pour refuser les cookies, mais cela peut affecter le fonctionnement du site.
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              10. Réclamation
            </Typography>
            <Typography variant="body1" paragraph>
              Si vous estimez que le traitement de vos données personnelles constitue une violation du RGPD, vous avez le droit d'introduire une réclamation auprès de la CNIL :
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Commission Nationale de l'Informatique et des Libertés (CNIL)</strong><br />
              3 Place de Fontenoy - TSA 80715 - 75334 PARIS CEDEX 07<br />
              Téléphone : 01 53 73 22 22<br />
              Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              11. Modifications
            </Typography>
            <Typography variant="body1" paragraph>
              Cette politique de confidentialité peut être mise à jour. Toute modification sera publiée sur cette page avec la date de mise à jour.
            </Typography>
          </Box>

          <Box sx={{ mt: 6, p: 3, backgroundColor: 'grey.100', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              <strong>Dernière mise à jour :</strong> {new Date().toLocaleDateString('fr-FR', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;